import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiError } from "../../api/errors";
import { imagesApi, putToSignedUrl, sha256Hex } from "../../api/images";
import { productsApi, type DraftVariantBody } from "../../api/products";
import type { ProcessingJobStatus } from "../../api/types";

/**
 * Creating a product that is photographed the ordinary way, one step at a time.
 *
 * THIS IS NOT THE GLASSES IMPORT AND DOES NOT SHARE ITS ORDER.
 *
 * `useGlassesImport` must upload and process a whole folder BEFORE the product
 * exists, because creating a frame takes the id of a batch the worker has
 * already finished. That order is forced by the frame intake and cannot be
 * rearranged for it — but it is exactly the order a cosmetic lens cannot meet:
 * how a photograph is treated is the family's decision, the family is read off
 * the product, and under that order the product does not exist yet.
 *
 * So this one is inverted, and that inversion is the only difference:
 *
 *   create the classified draft -> upload each photograph against it ->
 *   the worker processes it under the family the product already has
 *
 * Nothing here parses a folder, and no glasses module is imported: a lens
 * photograph is a photograph the administrator chose, not a file whose name has
 * to be interpreted to find out which view it is.
 *
 * Image bytes never pass through the backend. The signed URL for each
 * photograph lives inside one local binding for the length of one PUT and
 * nowhere else — not in state, not in the query cache, and never in a message.
 */

export type IntakePhase =
  | "idle"
  | "creating"
  | "uploading"
  | "processing"
  | "complete"
  | "failed";

/**
 * What each phase is called to the operator.
 *
 * `complete` is the name of an INTAKE phase, and the label has to say which
 * thing is complete. It read "Complete" beside a finished progress list, which
 * an administrator reasonably took to mean the product was finished — and then
 * went looking for it in the shop, where it was not, because intake ends at a
 * DRAFT and publication is a separate act on the product's own page. The phase
 * name is unchanged; only this sentence is, because nothing about the lifecycle
 * was wrong, just what the screen claimed about it.
 */
export const INTAKE_PHASE_LABELS: Record<IntakePhase, string> = {
  idle: "Ready",
  creating: "Creating the product",
  uploading: "Uploading photographs",
  processing: "Preparing product images",
  complete: "Draft created",
  failed: "Failed",
};

export interface PhotoProgress {
  /** The source role this photograph is uploaded under, sent literally. */
  role: string;
  /** Which colour it is a photograph of. Two colours both have a `photo_1`, so
   *  the role alone does not identify a photograph on a multi-variant product. */
  variantIndex: number;
  variantName: string;
  label: string;
  fileName: string;
  status: "pending" | "sending" | "processing" | "done" | "failed";
}

export interface IntakeState {
  phase: IntakePhase;
  photos: PhotoProgress[];
  /** Set the moment the draft exists — before any photograph is sent, because
   *  from that point a failed upload is a product to go back to rather than
   *  work that vanished. */
  productId: string | null;
  message: string | null;
  error: unknown;
}

const IDLE: IntakeState = {
  phase: "idle",
  photos: [],
  productId: null,
  message: null,
  error: null,
};

/** How often a running job is asked about. Same interval as every other place
 *  in this dashboard that watches one. */
const POLL_MS = 2000;

/** One photograph the administrator chose, with the role it is sent under.
 *
 * `variantIndex` names WHICH variant it is a photograph OF — its position in
 * the `variants` list sent to create the product. A product with one variant
 * has one group and every photograph is index 0; a genuine multi-variant
 * accessory has one group per colour, and a photograph never crosses between
 * them. Getting this wrong is exactly the defect this field exists to remove:
 * every photograph used to be attached to `color_variants[0]`, so a second
 * colour was published with no image of its own and the phone refused the
 * whole catalogue page it appeared on. */
export interface IntakePhoto {
  role: string;
  label: string;
  file: File;
  variantIndex: number;
}

export interface IntakeInput {
  name: string;
  productCode: string;
  description: string;
  category: string;
  basePrice: number;
  salePrice: number | null;
  currency: string;
  variants: DraftVariantBody[];
  photos: IntakePhoto[];
}

function isTerminal(status: ProcessingJobStatus): boolean {
  return (
    status === "completed" ||
    status === "failed" ||
    status === "needs_review" ||
    status === "cancelled"
  );
}

export function useProductIntake() {
  const [state, setState] = useState<IntakeState>(IDLE);
  const queryClient = useQueryClient();
  const runningRef = useRef(false);
  const liveRef = useRef(true);

  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);

  const patch = useCallback((next: Partial<IntakeState>) => {
    if (!liveRef.current) return;
    setState((current) => ({ ...current, ...next }));
  }, []);

  const markPhoto = useCallback(
    (photo: IntakePhoto, status: PhotoProgress["status"]) => {
      if (!liveRef.current) return;
      setState((current) => ({
        ...current,
        photos: current.photos.map((row) =>
          row.role === photo.role && row.variantIndex === photo.variantIndex
            ? { ...row, status }
            : row,
        ),
      }));
    },
    [],
  );

  /** Follow one job to a state that will not change on its own. */
  const awaitJob = useCallback(async (jobId: string): Promise<void> => {
    for (;;) {
      if (!liveRef.current) return;
      const job = await imagesApi.processingJob(jobId);
      if (isTerminal(job.status)) {
        if (job.status === "failed") {
          throw new Error(
            job.report?.errors?.[0] ?? "the photograph could not be processed",
          );
        }
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    }
  }, []);

  const start = useCallback(
    async (input: IntakeInput) => {
      if (runningRef.current) return;
      runningRef.current = true;

      setState({
        phase: "creating",
        photos: input.photos.map((photo) => ({
          role: photo.role,
          variantIndex: photo.variantIndex,
          variantName: input.variants[photo.variantIndex]?.name ?? "",
          label: photo.label,
          fileName: photo.file.name,
          status: "pending",
        })),
        productId: null,
        message: null,
        error: null,
      });

      try {
        // 1. The classified draft. Category is set here rather than patched
        //    afterwards, because every upload below is refused until the
        //    product has a family — which is the whole point of this order.
        const created = await productsApi.createDraft({
          name: input.name,
          product_code: input.productCode || null,
          base_price: input.basePrice,
          sale_price: input.salePrice,
          currency: input.currency,
          category: input.category,
          variants: input.variants,
        });

        const productId = created.product.id;
        patch({ productId });

        // 2. The description, as the draft's first edit — creation does not
        //    take one, exactly as in the glasses flow.
        let version = created.draft_revision.version;
        if (input.description) {
          const updated = await productsApi.updateDraft(productId, {
            expected_version: version,
            description: input.description,
          });
          version = updated.draft_revision.version;
        }

        // The colours the backend actually created, in the order they were
        // sent. Each photograph is attached to ITS OWN colour: a photograph of
        // the silver chain is not a photograph of the gold one, and attaching
        // it to whichever colour happened to be first is what published a
        // colour with no image of its own.
        const created_variants = created.draft_revision.color_variants ?? [];
        if (created_variants.length === 0) {
          throw new Error(
            "The product was created without a colour, so there is nothing to " +
              "attach a photograph to.",
          );
        }

        // 3. Each photograph, under the role the administrator chose it for,
        //    against the colour they chose it for. Roles are sent literally —
        //    nothing here translates photo_1 into a frame's front view.
        patch({ phase: "uploading" });
        for (const photo of input.photos) {
          const variantId = created_variants[photo.variantIndex]?.id;
          if (!variantId) {
            throw new Error(
              `The colour this photograph belongs to was not created, so ` +
                `there is nothing to attach ${photo.label} to.`,
            );
          }
          markPhoto(photo, "sending");
          const checksum = await sha256Hex(photo.file);

          const initiated = await imagesApi.initiateUpload(productId, {
            expected_version: version,
            image_role: photo.role,
            variant_id: variantId,
            original_filename: photo.file.name,
            content_type: photo.file.type,
            file_size: photo.file.size,
            ...(checksum ? { checksum } : {}),
          });

          // --- the signed URL lives only in this local binding ---
          await putToSignedUrl(initiated, photo.file);

          const completion = await imagesApi.completeUpload(productId, {
            // initiate does not bump the draft; completion does.
            expected_version: initiated.revision_version,
            draft_image_upload_id: initiated.draft_image_upload_id,
          });

          markPhoto(photo, "processing");
          patch({ phase: "processing" });
          await awaitJob(completion.processing_job_id);
          markPhoto(photo, "done");

          // Re-read rather than counted forward: completion bumped the draft,
          // and an assumed +1 would send a version the server rejects halfway
          // through — leaving a product with some of its photographs.
          const current = await productsApi.detail(productId);
          const next = current.draft_revision?.version;
          if (next === undefined) {
            throw new Error("The draft was closed while photographs were uploading.");
          }
          version = next;
        }

        await queryClient.invalidateQueries({ queryKey: ["products"] });
        runningRef.current = false;
        patch({ phase: "complete", productId });
      } catch (error) {
        runningRef.current = false;
        if (!liveRef.current) return;
        setState((current) => ({
          ...current,
          phase: "failed",
          error,
          photos: current.photos.map((photo) =>
            photo.status === "sending" || photo.status === "processing"
              ? { ...photo, status: "failed" }
              : photo,
          ),
          message:
            error instanceof ApiError
              ? error.userMessage
              : error instanceof Error && error.message
                ? error.message
                : "The product could not be finished.",
        }));
      }
    },
    [awaitJob, markPhoto, patch, queryClient],
  );

  const reset = useCallback(() => {
    runningRef.current = false;
    setState(IDLE);
  }, []);

  return { state, start, reset };
}
